import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Edit, Calendar, Users, Shield } from "lucide-react";
import { Client } from "@shared/schema";
import { calendarService } from "@/lib/calendarService";
import { useQueryClient } from "@tanstack/react-query";
import { ProfilePictureUpload } from "@/components/ui/profile-picture-upload";
import { formatDateSafe } from "@/lib/utils";
import type { EmployeeHeadcount } from "@/lib/employeeHeadcount";

interface ClientHeaderProps {
  client: Client & { logoUrl?: string | null };
  /** Shared headcount (DEV-329) — same numbers the HR/Compliance/OE tabs show. */
  employeeHeadcount: EmployeeHeadcount;
  activePlansCount: number;
  /** Linked plans whose plan year hasn't started yet — shown next to the
   * active count so "0 active plans" doesn't read as "no plans" (DEV-315). */
  upcomingPlansCount?: number;
  monthlyPremiumTotal: number;
  monthlyPremiumLoading?: boolean;
  onEditClick: () => void;
}

// Status → accent-rail color. Unknown statuses default to active green so the
// rail never blanks out.
function railColorFor(status?: string | null): string {
  switch ((status ?? "").toLowerCase()) {
    case "inactive":
    case "terminated":
    case "lost":
      return "#B91C1C";
    case "at_risk":
    case "at-risk":
    case "prospect":
      return "#B45309";
    default:
      return "#059669";
  }
}

function initialsFor(name: string | null | undefined): string {
  if (!name) return "?";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function formatPremium(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "—";
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

const ClientHeader = ({
  client,
  employeeHeadcount,
  activePlansCount,
  upcomingPlansCount = 0,
  monthlyPremiumTotal,
  monthlyPremiumLoading = false,
  onEditClick,
}: ClientHeaderProps) => {
  const queryClient = useQueryClient();

  const handleLogoChange = async () => {
    queryClient.invalidateQueries({ queryKey: ["/api/clients", client.id.toString()] });
  };

  const status = client?.status || "active";
  const rail = railColorFor(status);
  const sinceDate =
    (client as any).clientSince || (client as any).createdAt || null;
  const metaParts = [
    sinceDate ? `Client since ${formatDateSafe(sinceDate)}` : null,
    client?.state ? client.state : null,
  ].filter(Boolean);

  return (
    <div
      className="bg-white dark:bg-gray-900 rounded-[14px] border border-[#E5E7EB] dark:border-gray-700 overflow-hidden flex"
    >
      {/* Accent rail */}
      <div className="w-[5px] shrink-0" style={{ backgroundColor: rail }} aria-hidden />

      <div className="flex-1 flex items-center gap-4 px-[22px] py-[18px]">
        {/* Monogram / logo */}
        <div className="shrink-0">
          <ProfilePictureUpload
            currentPicture={client.logoUrl ?? client.logo}
            firstName={client.name}
            lastName=""
            onPictureChange={handleLogoChange}
            uploadEndpoint={`/api/clients/${client.id}/logo`}
            deleteEndpoint={`/api/clients/${client.id}/logo`}
            fetchUrlEndpoint={`/api/clients/${client.id}/logo-url`}
            fieldName="logo"
            size="lg"
            variant="logo"
            fallbackIcon={
              <div
                className="flex items-center justify-center w-full h-full text-white font-bold"
                style={{
                  background: "linear-gradient(135deg, #10B981, #047857)",
                  fontSize: "20px",
                  letterSpacing: "-0.5px",
                }}
              >
                {initialsFor(client.name)}
              </div>
            }
            showEditOnHover={true}
          />
        </div>

        {/* Identity */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[9px]">
            <h1
              className="font-bold text-[#0F172A] dark:text-gray-100 truncate"
              style={{ fontSize: "20px", letterSpacing: "-0.3px" }}
            >
              {client?.name || "Loading..."}
            </h1>
            <span
              className="inline-flex items-center gap-1.5 rounded-full font-semibold"
              style={{
                backgroundColor: "#ECFDF5",
                color: "#065F46",
                fontSize: "11px",
                padding: "2px 9px",
              }}
            >
              <span
                className="inline-block rounded-full"
                style={{ width: 6, height: 6, backgroundColor: rail }}
              />
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
          {metaParts.length > 0 && (
            <p
              className="mt-[5px] text-[#475569] dark:text-gray-400"
              style={{ fontSize: "12.5px" }}
            >
              {metaParts.map((part, idx) => (
                <span key={idx}>
                  {idx > 0 && (
                    <span className="mx-2" style={{ color: "#94A3B8" }}>·</span>
                  )}
                  {part}
                </span>
              ))}
            </p>
          )}
        </div>

        {/* Hero premium */}
        <div
          className="shrink-0 hidden md:block"
          style={{
            paddingRight: "22px",
            marginRight: "22px",
            borderRight: "1px solid #EEF0F4",
          }}
        >
          <div
            className="font-semibold uppercase"
            style={{
              fontSize: "10.5px",
              letterSpacing: "0.4px",
              color: "#94A3B8",
            }}
          >
            Monthly premium
          </div>
          {monthlyPremiumLoading ? (
            // Skeleton bar sized to roughly match a 6-digit dollar value so the
            // header doesn't reflow when the real number lands.
            <div
              className="mt-1 animate-pulse rounded bg-[#E5E7EB] dark:bg-gray-700"
              style={{ width: "120px", height: "28px" }}
              aria-label="Loading monthly premium"
            />
          ) : (
            <div
              className="font-bold text-[#0F172A] dark:text-gray-100 tabular-nums"
              style={{ fontSize: "28px", letterSpacing: "-0.8px", lineHeight: 1.1 }}
            >
              {formatPremium(monthlyPremiumTotal)}
            </div>
          )}
        </div>

        {/* Supporting stats */}
        <div className="shrink-0 hidden lg:flex flex-col gap-2" style={{ marginRight: "22px" }}>
          <div className="flex items-center gap-2" data-testid="header-employee-count">
            <Users className="h-[14px] w-[14px]" style={{ color: "#2563EB" }} />
            <span
              className="font-bold text-[#0F172A] dark:text-gray-100 tabular-nums"
              style={{ fontSize: "15px" }}
            >
              {employeeHeadcount.active}
            </span>
            <span style={{ fontSize: "11.5px", color: "#94A3B8" }}>
              {employeeHeadcount.active === 1 ? "active employee" : "active employees"}
              {employeeHeadcount.pendingActivation > 0 && (
                <span data-testid="header-pending-employees">
                  {" "}· {employeeHeadcount.pendingActivation} pending activation
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="h-[14px] w-[14px]" style={{ color: "#059669" }} />
            <span
              className="font-bold text-[#0F172A] dark:text-gray-100 tabular-nums"
              style={{ fontSize: "15px" }}
            >
              {activePlansCount}
            </span>
            <span style={{ fontSize: "11.5px", color: "#94A3B8" }}>
              {activePlansCount === 1 ? "active plan" : "active plans"}
              {upcomingPlansCount > 0 && (
                <span data-testid="header-upcoming-plans"> · {upcomingPlansCount} upcoming</span>
              )}
            </span>
          </div>
        </div>

        {/* Stacked actions */}
        <div className="shrink-0 flex flex-col gap-2">
          <Button
            onClick={() =>
              calendarService.scheduleMeeting({
                type: "client",
                id: client.id,
                name: client.name,
                email: client.contactEmail || undefined,
                phone: client.contactPhone || undefined,
              })
            }
            style={{ backgroundColor: "#1D3FBD" }}
            className="text-white hover:opacity-90 justify-center"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Schedule meeting
          </Button>
          <PermissionButton
            permission="clients:edit"
            whenDenied="hide"
            variant="outline"
            onClick={onEditClick}
            className="justify-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit client
          </PermissionButton>
        </div>
      </div>
    </div>
  );
};

export default ClientHeader;
